"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { format } from "date-fns"
import { CalendarIcon, Check, ChevronDown, Plus, X } from "lucide-react"
import { useRouter } from "next/navigation"
import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useForm, FormProvider } from "react-hook-form"
import * as z from "zod"
import { Button } from "../../../../shared/components/ui/button"
import { Calendar } from "../../../../shared/components/ui/calendar"
import { WorkOrderHoursDatepicker } from "../work-order-hours-datepicker"
import { Checkbox } from "../../../../shared/components/ui/checkbox"
import { MultiSelectWithFilters, FilterableOption } from "../../../../shared/components/ui/multi-select-with-filters"
import { SingleSelectWithFilters } from "../../../../shared/components/ui/single-select-with-filters"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../../../shared/components/ui/dialog"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../../../../shared/components/ui/form"
import { Input } from "../../../../shared/components/ui/input"
import { Label } from "../../../../shared/components/ui/label"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../../../shared/components/ui/popover"
import { Textarea } from "../../../../shared/components/ui/textarea"
import { TypographyH4 } from "../../../../shared/components/ui/typography"
import errorToast from "../../../../shared/lib/toast/error.toast"
import successToast from "../../../../shared/lib/toast/success.toast"
import { cn } from "../../../../shared/lib/utils"
import { useLightweightHomes, LightweightHomesResponse } from "../../../homeowners/hooks/lightweight-homes.hook"
import { useVolunteersAndTeamLeaders } from "../../../volunteers/hooks/volunteers.hooks"
import { useTeamLeads } from "../../hooks/use-team-leads.hook"
import { useAdminsAndBaseLeaders } from "../../hooks/use-admins-base-leaders.hook"
import { useSession } from "../../../auth/context/session.context"
import { UserRole } from "../../../../shared/constants/user-roles"
import FormCard from "../../ui/form-card"
import { DirtyFieldsProvider, useDirtyFields } from "../../../../shared/context/dirty-fields-context"
import StickySaveBar from "../../../../shared/components/sticky-save-bar"
import { updateHome } from "@/features/homeowners/api/homes.api"
import { getHome } from "@/features/homeowners/api/homeowners.api"
import {
    DateRangePickerControl,
    type DateRangePickerValue,
} from "@/shared/components/form-inputs/date-range-picker-control"

import { useWorkScope, useWorkScopes } from "../../hooks/use-work-scopes"
import { useRegionId } from "@/shared/hooks/use-region-id"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../../shared/components/ui/accordion"
import { TOOL_KIT_CONTENTS, TOOL_KIT_NAME, isToolKit, sortToolsWithToolKitFirst } from "../../constants/tool-kit-contents"
import {
    isBlankLineName,
    stripBlankLineItems,
    stripBlankLineNames,
} from "../../utils/tools-supplies-blank-lines"

// Helper component to wrap inputs with dirty field styling
const DirtyAwareInput = ({ 
    fieldName, 
    children, 
    className,
    ...props 
}: { 
    fieldName: string
    children?: React.ReactNode
    className?: string
    [key: string]: any 
}) => {
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(fieldName)
    
    if (React.isValidElement(children)) {
        return React.cloneElement(children as React.ReactElement<any>, {
            className: cn(
                children.props.className,
                isDirty && "border-[#9c27b0] focus:border-[#9c27b0] focus:ring-[#9c27b0]/20 shadow-sm transition-colors duration-200"
            )
        })
    }
    
    return <div className={cn(className, isDirty && "border-[#9c27b0]")}>{children}</div>
}

// Helper component for labels that turn purple when field is dirty
const DirtyAwareLabel = ({ 
    fieldName, 
    children,
    className 
}: { 
    fieldName: string
    children: React.ReactNode
    className?: string
}) => {
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(fieldName)
    
    return (
        <FormLabel className={cn(
            "transition-colors duration-200",
            isDirty && "text-[#9c27b0]",
            className
        )}>
            {children}
        </FormLabel>
    )
}



// Single-select component
const SingleSelect = ({
    options,
    selectedValue,
    onSelectionChange,
    placeholder,
    searchPlaceholder = "Search...",
}: {
    options: { id: string; name: string; role?: string }[]
    selectedValue: string
    onSelectionChange: (value: string) => void
    placeholder: string
    searchPlaceholder?: string
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")

    const filteredOptions = options.filter(
        (option) =>
            option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (option.role &&
                option.role.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    const handleSelect = (value: string) => {
        onSelectionChange(value)
        setSearchTerm("")
        setIsOpen(false)
    }

    const selectedName = selectedValue
        ? options.find((option) => option.id === selectedValue)?.name
        : ""

    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open)
                if (!open) {
                    setSearchTerm("")
                }
            }}
        >
            <PopoverTrigger asChild>
                <div
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls="combobox-popover-content"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            setIsOpen(!isOpen)
                        }
                    }}
                    className={cn(
                        "flex h-14 w-full items-center justify-between whitespace-nowrap rounded border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 cursor-pointer data-[placeholder]:text-muted-foreground",
                        isOpen && "border-primary",
                        "focus:border-primary"
                    )}
                >
                    <span
                        className={
                            selectedName
                                ? "text-foreground"
                                : "text-muted-foreground/60"
                        }
                    >
                        {selectedName || placeholder}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
                <div className="p-2">
                    <Input
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) =>
                            setSearchTerm((e.target as HTMLInputElement).value)
                        }
                        className="mb-2 h-9 text-sm"
                    />
                    <div className="max-h-60 overflow-auto">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.id}
                                    className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer"
                                    onClick={() => handleSelect(option.id)}
                                >
                                    <div className="flex items-center justify-center w-4 h-4">
                                        {selectedValue === option.id && (
                                            <Check className="w-4 h-4 text-primary" />
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-sm text-foreground">
                                            {option.name}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                                No team lead found
                            </div>
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}

// Validation schema
const workOrderSchema = z.object({
    home: z.string().min(1, "Home is required"),
    workOrderType: z.string().optional(), // Make optional, we'll handle validation manually
    ownerId: z.number().optional(),
    workOrderHours: z.array(z.object({
        date: z.date().nullable(),
        hoursWorked: z.number().min(0)
    })).optional(),
    teamName: z.string().optional(),
    team: z.array(z.string()).optional(),
    teamLeadId: z.number().optional(),
    volunteersNeeded: z.number().optional(),
    projectedVolunteerHours: z.number().optional(),
    projectDescription: z.string().optional(),
    notes: z.string().optional(),
    toolsNeeded: z.union([
        z.array(z.object({
            name: z.string(),
            out: z.boolean(),
            in: z.boolean()
        })),
        z.array(z.string()),
        z.string()
    ]).optional(),
    suppliesNeeded: z.union([
        z.array(z.object({
            name: z.string(),
            out: z.boolean(),
            in: z.boolean()
        })),
        z.array(z.string()),
        z.string()
    ]).optional(),
})

type WorkOrderFormData = z.infer<typeof workOrderSchema>

// Simple checkbox list component using shadcn components
const CheckboxList = ({
    title,
    options,
    className = "",
    value = [],
    onChange,
    onAddCustom,
    fieldName,
}: {
    title: string
    options: string[]
    className?: string
    value?: string[]
    onChange?: (value: string[]) => void
    onAddCustom?: () => void
    fieldName?: string
}) => {
    const { isFieldDirty } = useDirtyFields()
    const isDirty = fieldName ? isFieldDirty(fieldName) : false
    
    const handleToggle = (option: string) => {
        if (!onChange) return

        const newValue = value.includes(option)
            ? value.filter((item) => item !== option)
            : [...value, option]
        onChange(newValue)
    }

    // Combine options with manually added items that aren't in options
    // For tools, sort so Tool Kit is always first
    const allItems = useMemo(() => {
        const filteredOptions = stripBlankLineNames(options)
        const manuallyAdded = value.filter(
            (item) => !filteredOptions.includes(item) && !isBlankLineName(item)
        )
        const combined = [...filteredOptions, ...manuallyAdded]
        // Sort tools with Tool Kit first (only for Tools, not Supplies)
        if (title === "Tools") {
            return sortToolsWithToolKitFirst(combined)
        }
        return combined
    }, [options, value, title])

    return (
        <div className={`space-y-1 ${className}`}>
            <Label className={cn(
                "text-sm pl-1 transition-colors duration-200",
                isDirty && "text-[#9c27b0]"
            )}>
                {title} Needed
            </Label>
            <FormCard>
                <div className="flex flex-col gap-4">
                    {/* All items (default options + manually added) */}
                    {allItems.map((option, index) => {
                        const isManuallyAdded = !options.includes(option)
                        return (
                            <div key={`${option}-${index}`} className="flex flex-col gap-2">
                                <Label
                                    htmlFor={`${option}-${index}`}
                                    className="text-sm flex gap-2 items-center cursor-pointer"
                                >
                                    <Checkbox
                                        id={`${option}-${index}`}
                                        className="rounded-[5px] border-primary"
                                        checked={value.includes(option)}
                                        onCheckedChange={() => handleToggle(option)}
                                    />
                                    <span
                                        className={cn(
                                            value.includes(option)
                                                ? "text-foreground"
                                                : "text-muted-foreground",
                                        )}
                                        style={
                                            !value.includes(option)
                                                ? { textDecoration: "line-through" }
                                                : {}
                                        }
                                    >
                                        {option}
                                    </span>
                                </Label>
                                {title === "Tools" && isToolKit(option) && value.includes(option) && (
                                    <div className="pl-7">
                                        <Accordion type="single" collapsible className="w-full">
                                            <AccordionItem value="tool-kit-contents" className="border rounded-md px-3">
                                                <AccordionTrigger className="text-xs py-2">
                                                    View Tool Kit Contents
                                                </AccordionTrigger>
                                                <AccordionContent className="pt-2 pb-4">
                                                    <div className="space-y-4 text-sm">
                                                        {TOOL_KIT_CONTENTS.map((section, sectionIndex) => (
                                                            <div key={sectionIndex} className="space-y-2">
                                                                <h4 className="font-semibold text-xs">{section.title}</h4>
                                                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                                                    {section.items.map((item, itemIndex) => (
                                                                        <div key={itemIndex} className="flex justify-between">
                                                                            <span className="text-gray-700">{item.name}</span>
                                                                            <span className="text-gray-500 font-medium">{item.quantity}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </AccordionContent>
                                            </AccordionItem>
                                        </Accordion>
                                    </div>
                                )}
                            </div>
                        )
                    })}

                    {/* Add button - now functional */}
                    <div className="flex justify-start">
                        <Button
                            type="button"
                            className="h-8 px-3 text-sm text-foreground"
                            variant="secondary"
                            size="sm"
                            showLoader={false}
                            onClick={onAddCustom}
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Add {title}
                        </Button>
                    </div>
                </div>
            </FormCard>
        </div>
    )
}

interface WorkOrderFormProps {
    workOrderType?: string
    mode?: "create" | "edit"
    workOrder?: any // The complete work order object for edit mode
    selectedWorkScope?: string
    onWorkScopeChange?: (workScope: string) => void
    onValidateWorkScope?: () => boolean
}

function WorkOrderForm({
    workOrderType,
    mode = "create",
    workOrder,
    selectedWorkScope,
    onWorkScopeChange,
    onValidateWorkScope,
}: WorkOrderFormProps) {
    const router = useRouter()
    
    // Get current user session
    const { session, profile} = useSession()

    // Check if user can add notes (Volunteer, Team Leader, Base Leader, or Admin)
    const isVolunteer = session?.role === UserRole.VOLUNTEER
    const isTeamLeader = session?.role === UserRole.TEAM_LEADER
    const isBaseLeader = session?.role === UserRole.BASE_LEADER
    const isAdmin = session?.role === UserRole.ADMIN
    const canAddNotes = isVolunteer || isTeamLeader || isBaseLeader || isAdmin

    // Notes/Status/Next Steps state (separate from form, saves to Project General Notes)
    const [workOrderNote, setWorkOrderNote] = useState("")
    const [originalWorkOrderNote, setOriginalWorkOrderNote] = useState("")

    // Format date stamp for notes (MM/DD/YY UserName:)
    const formatDateStamp = () => {
        const now = new Date()
        const month = String(now.getMonth() + 1).padStart(2, '0')
        const day = String(now.getDate()).padStart(2, '0')
        const year = String(now.getFullYear()).slice(-2)
        const userName = profile?.fullname || 'Unknown User'
        return `${month}/${day}/${year} ${userName}:`
    }

    // Check if user is TeamLeader and if this is their work order
    const isTeamLeaderRestricted = mode === "edit" && 
                                   session?.role === UserRole.TEAM_LEADER &&
                                   workOrder?.teamLead?.id === profile?.id

    // Fetch team members from the database (includes Volunteers and TeamLeaders)
    const { data: volunteersData, isLoading: isLoadingVolunteers } =
        useVolunteersAndTeamLeaders()

    // Fetch team leads from the database using the team leads hook
    const { data: teamLeadsData, isLoading: isLoadingTeamLeads } =
        useTeamLeads()

    // Fetch admins and base leaders for the owner dropdown
    const { data: ownersData, isLoading: isLoadingOwners } =
        useAdminsAndBaseLeaders()

    // Fetch lightweight homes data for dropdowns
    const { regionId, regionSlug, isLoading: isRegionLoading } = useRegionId()
    const shouldFetchHomes = !isRegionLoading && regionId !== null && regionId !== undefined
    const { data: homesData, isLoading: isLoadingHomes } = useLightweightHomes(
        shouldFetchHomes ? regionId ?? undefined : undefined,
        { enabled: shouldFetchHomes }
    )

    // Transform homes data into options for Home single-select
    const homeOptions: { id: string; name: string }[] = useMemo(() => {
        if (!homesData) return []
        
        const response = homesData as LightweightHomesResponse
        if (!response.success || !response.data) return []

        return response.data.map((home: any) => ({
            id: home.id.toString(),
            name: `${home.homeowner?.user?.fullname || 'Unknown'} - ${home.address}${home.homeowner?.user?.phone ? ` (${home.homeowner.user.phone})` : ''}`,
        }))
    }, [homesData])

    // Extract organization options from volunteers data
    const organizationOptions = useMemo(() => {
        if (!volunteersData?.success || !volunteersData.data) return []
        const orgs = new Set<string>()
        volunteersData.data.volunteers.forEach((volunteer: any) => {
            if (volunteer.user?.organizationName) {
                orgs.add(volunteer.user.organizationName)
            }
        })
        return Array.from(orgs).sort()
    }, [volunteersData])

    // Team lead options: Admin, BaseLeader, TeamLeader from volunteers-and-team-leaders (same shape as Volunteers for filters/display).
    // If the current work order's team lead isn't in that list, prepend from teamLeadsData so the selection is always visible.
    const teamLeadOptions = useMemo((): FilterableOption[] => {
        const teamLeadRoles = new Set(["Admin", "BaseLeader", "TeamLeader"])
        if (!volunteersData?.success || !volunteersData.data) return []
        const base = volunteersData.data.volunteers
            .filter((v: any) => teamLeadRoles.has(v.user?.role?.name))
            .map((v: any) => ({
                id: v.id.toString(),
                name: v.user?.fullname ?? "",
                role: v.user?.role?.name || undefined,
                organization: v.user?.organizationName || undefined,
                arrivalDate: v.user?.availabilityStart
                    ? new Date(v.user.availabilityStart).toISOString().split("T")[0]
                    : undefined,
                departureDate: v.user?.availabilityEnd
                    ? new Date(v.user.availabilityEnd).toISOString().split("T")[0]
                    : undefined,
            }))
        const ids = new Set(base.map((o: FilterableOption) => o.id))
        const selectedId = workOrder?.teamLead?.id?.toString()
        if (selectedId && !ids.has(selectedId) && Array.isArray(teamLeadsData) && teamLeadsData.length) {
            const t = teamLeadsData.find((tl: any) => tl.id.toString() === selectedId)
            if (t) {
                base.unshift({
                    id: t.id.toString(),
                    name: t.fullname ?? "",
                    role: undefined,
                    organization: undefined,
                    arrivalDate: undefined,
                    departureDate: undefined,
                })
            }
        }
        return base
    }, [volunteersData, workOrder?.teamLead?.id, teamLeadsData])

    // Owner options: Admins and BaseLeaders as FilterableOption for SingleSelectWithFilters.
    // If the current work order's owner isn't in the list, prepend so the selection is always visible.
    const ownerOptions = useMemo((): FilterableOption[] => {
        if (!ownersData?.success || !ownersData.data) return []
        const base = ownersData.data.map((owner: any) => ({
            id: owner.id.toString(),
            name: owner.fullname ?? "",
            role: owner.role?.name ?? undefined,
            organization: owner.organizationName ?? undefined,
            arrivalDate: undefined,
            departureDate: undefined,
        }))
        const ids = new Set(base.map((o: FilterableOption) => o.id))
        const selectedId = workOrder?.owner?.id?.toString()
        if (selectedId && !ids.has(selectedId)) {
            base.unshift({
                id: selectedId,
                name: workOrder.owner?.fullname ?? "",
                role: undefined,
                organization: undefined,
                arrivalDate: undefined,
                departureDate: undefined,
            })
        }
        return base
    }, [ownersData, workOrder?.owner?.id, workOrder?.owner?.fullname])

    // Organization options for Owner filter (from owners' organizationName)
    const ownerOrganizationOptions = useMemo(() => {
        if (!ownersData?.success || !ownersData.data) return []
        const orgs = new Set<string>()
        ownersData.data.forEach((u: any) => {
            if (u.organizationName) orgs.add(u.organizationName)
        })
        return Array.from(orgs).sort()
    }, [ownersData])

    // Fetch work scope options for display
    const { workScopeOptions } = useWorkScopes()
    
    // Fetch tools and supplies from database for the selected work scope (only for create mode)
    const currentWorkOrderType = mode === "edit" && workOrder?.workOrderType
        ? workOrder.workOrderType
        : workOrderType
    const workScope = useWorkScope(selectedWorkScope ?? null)
    const dbWorkScope = workScope?.workScope

    // Work order hours state
    const [workOrderHours, setWorkOrderHours] = useState<Array<{ date: Date | null; hoursWorked: number }>>([{ date: null, hoursWorked: 0 }])
    const [originalWorkOrderHours, setOriginalWorkOrderHours] = useState<Array<{ date: Date | null; hoursWorked: number }>>([{ date: null, hoursWorked: 0 }])
    const [addWorkDaysRange, setAddWorkDaysRange] = useState<DateRangePickerValue>({})

    // Helper to check if a specific work hours field is modified
    const isWorkHoursFieldModified = (index: number, field: 'date' | 'hoursWorked'): boolean => {
        if (!originalWorkOrderHours[index]) return false // Return false if original doesn't exist
        if (!workOrderHours[index]) return false
        
        if (field === 'date') {
            // For dates, compare using getTime() or both null
            const current = workOrderHours[index]?.date
            const original = originalWorkOrderHours[index]?.date
            
            // Both null
            if (current === null && original === null) return false
            // One null, one not
            if ((current === null) !== (original === null)) return true
            // Both exist, compare timestamps
            if (current && original) {
                return current.getTime() !== original.getTime()
            }
            return false
        }
        return workOrderHours[index]?.[field] !== originalWorkOrderHours[index]?.[field]
    }

    // Helper to check if a date is required (has hours but no date)
    const isDateRequired = (index: number): boolean => {
        const entry = workOrderHours[index]
        return !!(entry && entry.hoursWorked && entry.hoursWorked > 0 && !entry.date)
    }

    // Helper to format date to YYYY-MM-DD using local timezone
    const formatDateForDB = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    const getDatesInRange = (from: Date, to: Date): Date[] => {
        const start = new Date(from.getFullYear(), from.getMonth(), from.getDate())
        const end = new Date(to.getFullYear(), to.getMonth(), to.getDate())
        const dates: Date[] = []
        const current = new Date(start)

        while (current <= end) {
            dates.push(new Date(current))
            current.setDate(current.getDate() + 1)
        }

        return dates
    }

    // Loading state for form submission
    const [isLoading, setIsLoading] = useState(false)

    // Dirty fields tracking for edit mode
    const [isFormClean, setIsFormClean] = useState(true)
    const [showSuccessBar, setShowSuccessBar] = useState(false)

    // Error states for number fields
    const [volunteersNeededError, setVolunteersNeededError] = useState("")
    const [projectedVolunteerHoursError, setProjectedVolunteerHoursError] =
        useState("")

    // Modal state for adding custom tools/supplies
    const [isAddCustomModalOpen, setIsAddCustomModalOpen] = useState(false)
    const [customToolInput, setCustomToolInput] = useState("")
    const [modalType, setModalType] = useState<"tool" | "supply">("tool")

    // Check if we're in edit mode and still loading required data
    const isEditModeLoading =
        mode === "edit" &&
        (!volunteersData?.success || !teamLeadsData || (shouldFetchHomes && !homesData?.success) || !ownersData)

    // Check if we're in create mode and still loading required data (optional, for better UX)
    const isCreateModeLoading =
        mode === "create" &&
        (!volunteersData?.success || !teamLeadsData || (shouldFetchHomes && !homesData?.success) || !ownersData)

    // Check if any of the required data failed to load
    const hasDataLoadError =
        (volunteersData && !volunteersData.success) ||
        teamLeadsData === null ||
        teamLeadsData === undefined ||
        (shouldFetchHomes && homesData && !homesData.success)

    // Helper function to get the current work order type
    const getCurrentWorkOrderType = useCallback(() => {
        return mode === "edit" && workOrder?.workOrderType
            ? workOrder.workOrderType
            : workOrderType
    }, [mode, workOrder?.workOrderType, workOrderType])

    // Get the appropriate tools and supplies options from database (only for create mode)
    const getToolsOptions = useCallback(() => {
        // For edit mode, we don't need work scope defaults
        if (mode === "edit") return []
        // Return tools from database if available, otherwise empty array
        const tools = stripBlankLineNames(dbWorkScope?.toolsNeeded || [])
        // Sort so Tool Kit is always first
        return sortToolsWithToolKitFirst(tools)
    }, [dbWorkScope, mode])

    // Function to handle adding custom tool or supply
    const handleAddCustomTool = () => {
        if (!customToolInput.trim()) {
            const itemType = modalType
            errorToast(`Please enter a ${itemType} name`)
            return
        }

        const newItem = customToolInput.trim()

        // Update the form value directly - convert to object format
        const newItemObject = {
            name: newItem,
            out: false,
            in: false
        }
        
        if (modalType === "supply") {
            const currentSupplies = form.getValues("suppliesNeeded") as Array<{name: string, out: boolean, in: boolean}> | string[] | string | undefined
            // Handle different formats - convert to object array
            const suppliesArray = Array.isArray(currentSupplies)
                ? currentSupplies.map((item: any) => 
                    typeof item === 'string' 
                        ? { name: item, out: false, in: false }
                        : item
                )
                : []
            form.setValue("suppliesNeeded", [...suppliesArray, newItemObject], { shouldDirty: true })
        } else {
            const currentTools = form.getValues("toolsNeeded") as Array<{name: string, out: boolean, in: boolean}> | string[] | string | undefined
            // Handle different formats - convert to object array
            const toolsArray = Array.isArray(currentTools)
                ? currentTools.map((item: any) => 
                    typeof item === 'string' 
                        ? { name: item, out: false, in: false }
                        : item
                )
                : []
            form.setValue("toolsNeeded", [...toolsArray, newItemObject], { shouldDirty: true })
        }

        // Close modal and reset input
        setIsAddCustomModalOpen(false)
        setCustomToolInput("")
        setModalType("tool")
        successToast(`Custom ${modalType} added successfully`)
    }


    // Function to open the add tool/supply modal
    const openAddCustomModal = (type: "tool" | "supply") => {
        setIsAddCustomModalOpen(true)
        setModalType(type)
        setCustomToolInput("")
    }

    const getSuppliesOptions = useCallback(() => {
        // For edit mode, we don't need work scope defaults
        if (mode === "edit") return []
        // Return supplies from database if available, otherwise empty array
        return stripBlankLineNames(dbWorkScope?.suppliesNeeded || [])
    }, [dbWorkScope, mode])

    const initialVolunteerSelection = useMemo(() => {
        if (mode !== "edit" || !workOrder) {
            return [] as string[]
        }

        if (Array.isArray((workOrder as any).volunteerIds) && (workOrder as any).volunteerIds.length > 0) {
            return (workOrder as any).volunteerIds
                .filter((id: unknown) => typeof id === "number" || typeof id === "string")
                .map((id: number | string) => id.toString())
        }

        if (Array.isArray((workOrder as any).volunteers) && (workOrder as any).volunteers.length > 0) {
            return (workOrder as any).volunteers
                .map((volunteer: any) => volunteer?.id)
                .filter((id: unknown) => typeof id === "number" || typeof id === "string")
                .map((id: number | string) => id.toString())
        }

        return [] as string[]
    }, [mode, workOrder])

    const form = useForm<WorkOrderFormData>({
        resolver: zodResolver(workOrderSchema),
        defaultValues: {
            home:
                mode === "edit" && workOrder?.home?.id
                    ? workOrder.home.id.toString()
                    : "",
            workOrderType:
                mode === "edit" && workOrder?.workOrderType
                    ? workOrder.workOrderType
                    : workOrderType || undefined,
            ownerId:
                mode === "edit" && workOrder?.owner?.id
                    ? workOrder.owner.id
                    : 0,
            workOrderHours:
                mode === "edit" && workOrder?.workOrderHours
                    ? (() => {
                          try {
                              // Parse workOrderHours if it's a JSON string
                              let hours = workOrder.workOrderHours
                              if (typeof hours === 'string') {
                                  hours = JSON.parse(hours)
                              }
                              // Map to Date objects - parse in local timezone to avoid day shifts
                              if (Array.isArray(hours) && hours.length > 0) {
                                  return hours.map((entry: any) => {
                                      if (!entry.date) {
                                          return { date: null, hoursWorked: entry.hoursWorked || 0 }
                                      }
                                      // Parse date string (YYYY-MM-DD) in local timezone
                                      const dateStr = entry.date.split("T")[0]
                                      const [year, month, day] = dateStr.split("-").map(Number)
                                      return {
                                          date: new Date(year, month - 1, day), // month is 0-indexed
                                          hoursWorked: entry.hoursWorked || 0,
                                      }
                                  })
                              }
                              return [{ date: null, hoursWorked: 0 }]
                          } catch (error) {
                              console.error("Error parsing workOrderHours data:", error)
                              return [{ date: null, hoursWorked: 0 }]
                          }
                      })()
                    : [{ date: null, hoursWorked: 0 }],
            teamName:
                mode === "edit" && workOrder?.teamName
                    ? workOrder.teamName
                    : "",
            team:
                mode === "edit"
                    ? initialVolunteerSelection
                    : [] as string[],
            teamLeadId:
                mode === "edit" && workOrder?.teamLead?.id
                    ? workOrder.teamLead.id
                    : 0,
            volunteersNeeded:
                mode === "edit" && workOrder?.volunteersNeeded
                    ? workOrder.volunteersNeeded
                    : undefined,
            projectedVolunteerHours:
                mode === "edit" && workOrder?.projectedVolunteerHours
                    ? workOrder.projectedVolunteerHours
                    : undefined,
            projectDescription:
                mode === "edit" && workOrder?.projectDescription
                    ? workOrder.projectDescription
                    : "",
            notes: mode === "edit" && workOrder?.notes ? workOrder.notes : "",
            toolsNeeded:
                mode === "edit" && workOrder?.toolsNeeded
                    ? stripBlankLineItems(
                        Array.isArray(workOrder.toolsNeeded)
                        ? workOrder.toolsNeeded.map((item: any) => {
                            // If it's already an object, use it
                            if (typeof item === 'object' && item !== null && 'name' in item) {
                                return item
                            }
                            // If it's a string, convert to object
                            return {
                                name: typeof item === 'string' ? item : String(item),
                                out: false,
                                in: false
                            }
                        })
                        : workOrder.toolsNeeded.split(", ").filter((item: string) => item.trim()).map((item: string) => ({
                            name: item,
                            out: false,
                            in: false
                        }))
                    )
                    : [], // Always start with empty array for create mode
            suppliesNeeded:
                mode === "edit" && workOrder?.suppliesNeeded
                    ? stripBlankLineItems(
                        Array.isArray(workOrder.suppliesNeeded)
                        ? workOrder.suppliesNeeded.map((item: any) => {
                            // If it's already an object, use it
                            if (typeof item === 'object' && item !== null && 'name' in item) {
                                return item
                            }
                            // If it's a string, convert to object
                            return {
                                name: typeof item === 'string' ? item : String(item),
                                out: false,
                                in: false
                            }
                        })
                        : workOrder.suppliesNeeded.split(", ").filter((item: string) => item.trim()).map((item: string) => ({
                            name: item,
                            out: false,
                            in: false
                        }))
                    )
                    : [], // Always start with empty array for create mode
        },
        mode: "onChange",
        shouldFocusError: false,
    })

    const addWorkDaysFromRange = (range: DateRangePickerValue) => {
        if (!range.from || !range.to) return

        const rangeDates = getDatesInRange(range.from, range.to)
        const existingRows = workOrderHours.filter(
            (entry) => entry.date || entry.hoursWorked > 0
        )
        const existingDateKeys = new Set(
            existingRows
                .filter((entry) => entry.date)
                .map((entry) => formatDateForDB(entry.date as Date))
        )
        const newRows = rangeDates
            .filter((date) => !existingDateKeys.has(formatDateForDB(date)))
            .map((date) => ({ date, hoursWorked: 0 }))

        const nextRows =
            existingRows.length || newRows.length
                ? [...existingRows, ...newRows]
                : [{ date: null, hoursWorked: 0 }]

        setWorkOrderHours(nextRows)
        form.setValue('workOrderHours', nextRows, { shouldDirty: true })
        setAddWorkDaysRange({})
    }

    // Update form workOrderType when selectedWorkScope changes
    useEffect(() => {
        if (mode === "create" && selectedWorkScope) {
            form.setValue("workOrderType", selectedWorkScope)
        }
    }, [selectedWorkScope, mode, form])

    // Track if tools/supplies have been loaded to prevent re-initialization
    const [toolsSuppliesLoaded, setToolsSuppliesLoaded] = useState(false)

    // Load tools and supplies when work scope is selected in create mode (only once)
    // In edit mode, NEVER reload from work scope - always use the saved work order data
    useEffect(() => {
        // Only load from work scope in create mode
        if (mode === "create" && selectedWorkScope && dbWorkScope && !toolsSuppliesLoaded) {
            const scopeTools = stripBlankLineNames(dbWorkScope.toolsNeeded || []).map((tool: string) => ({
                name: tool,
                out: false,
                in: false
            }))
            const scopeSupplies = stripBlankLineNames(dbWorkScope.suppliesNeeded || []).map((supply: string) => ({
                name: supply,
                out: false,
                in: false
            }))
            const sortedTools = sortToolsWithToolKitFirst(scopeTools) as Array<{name: string, out: boolean, in: boolean}>
            form.setValue("toolsNeeded", sortedTools)
            form.setValue("suppliesNeeded", scopeSupplies)
            setToolsSuppliesLoaded(true)
        }
        // In edit mode, strip legacy blank lines from saved work order data once on load
        if (mode === "edit" && workOrder && !toolsSuppliesLoaded) {
            const savedTools: Array<{name: string, out: boolean, in: boolean}> = Array.isArray(workOrder.toolsNeeded) 
                ? workOrder.toolsNeeded.map((item: any) => {
                    if (typeof item === 'object' && item !== null && 'name' in item) {
                        return {
                            name: item.name || item,
                            out: item.out || false,
                            in: item.in || false
                        }
                    }
                    return {
                        name: typeof item === 'string' ? item : String(item),
                        out: false,
                        in: false
                    }
                })
                : []
            const savedSupplies: Array<{name: string, out: boolean, in: boolean}> = Array.isArray(workOrder.suppliesNeeded)
                ? workOrder.suppliesNeeded.map((item: any) => {
                    if (typeof item === 'object' && item !== null && 'name' in item) {
                        return {
                            name: item.name || item,
                            out: item.out || false,
                            in: item.in || false
                        }
                    }
                    return {
                        name: typeof item === 'string' ? item : String(item),
                        out: false,
                        in: false
                    }
                })
                : []
            
            if (savedTools.length > 0) {
                const sortedTools = sortToolsWithToolKitFirst(stripBlankLineItems(savedTools)) as Array<{name: string, out: boolean, in: boolean}>
                form.setValue("toolsNeeded", sortedTools, { shouldDirty: false })
            }
            if (savedSupplies.length > 0) {
                form.setValue("suppliesNeeded", stripBlankLineItems(savedSupplies), { shouldDirty: false })
            }
            setToolsSuppliesLoaded(true)
        }
    }, [mode, selectedWorkScope, dbWorkScope, form, toolsSuppliesLoaded, workOrder])


    // Initialize workOrderHours state from form data only once on mount
    useEffect(() => {
        const formHours = form.getValues('workOrderHours')
        console.log('Initializing workOrderHours from form:', formHours)
        if (formHours && Array.isArray(formHours) && formHours.length > 0) {
            // Deep copy to ensure we're not sharing references
            // Don't use JSON.parse/stringify as it converts Dates to strings
            const deepCopy = formHours.map(entry => ({
                date: entry.date ? new Date(entry.date.getTime()) : null,
                hoursWorked: entry.hoursWorked
            }))
            const originalCopy = formHours.map(entry => ({
                date: entry.date ? new Date(entry.date.getTime()) : null,
                hoursWorked: entry.hoursWorked
            }))
            setWorkOrderHours(deepCopy)
            setOriginalWorkOrderHours(originalCopy)
            console.log('Set workOrderHours state:', deepCopy)
            console.log('Set originalWorkOrderHours state:', originalCopy)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Empty dependency array - only run once on mount



    // Track when form becomes dirty/clean
    const { formState: { isDirty } } = form

    // Check if note has changed
    const hasNoteChanges = workOrderNote.trim() !== originalWorkOrderNote.trim()

    // Combine form dirty state with note changes for sticky bar visibility
    const hasUnsavedChanges = isDirty || hasNoteChanges

    // Clear clean state when form becomes dirty or note changes
    useEffect(() => {
        if (hasUnsavedChanges && isFormClean) {
            setIsFormClean(false)
        }
    }, [hasUnsavedChanges, isFormClean])

    // Hide success bar when form becomes dirty again or note changes
    useEffect(() => {
        if (hasUnsavedChanges && showSuccessBar) {
            setShowSuccessBar(false)
        }
    }, [hasUnsavedChanges, showSuccessBar])


    const onSubmit = async (data: WorkOrderFormData) => {
        // Manual validation for work scope
        if (!data.workOrderType || data.workOrderType.trim() === "") {
            if (onValidateWorkScope) {
                onValidateWorkScope()
            }
            return
        }
        
        // Validate work scope selection first
        if (onValidateWorkScope && !onValidateWorkScope()) {
            return
        }

        // Check for number field errors before submitting
        if (volunteersNeededError || projectedVolunteerHoursError) {
            errorToast("Please fix the number field errors before submitting")
            return
        }

        // Validate workOrderHours - cannot have hours without a date
        const hasHoursWithoutDate = data.workOrderHours?.some(
            entry => entry.hoursWorked && entry.hoursWorked > 0 && !entry.date
        )
        if (hasHoursWithoutDate) {
            errorToast("Please select a date for all entries with hours worked")
            return
        }

        setIsLoading(true)

        // Prepare data with only the fields that exist in the DB
        const baseWorkOrderData = {
            workOrderType: getCurrentWorkOrderType() || "blank",
            workScopeId: dbWorkScope?.id || null, // Add work scope ID for proper table linking
            homeId: Number(data.home), // Convert to number and map to homeId
            ownerId: data.ownerId || 0, // Work order owner (Admin or BaseLeader), send 0 if not selected
            teamName: data.teamName, // Team name
            teamLeadId: data.teamLeadId || 0, // Send team lead ID as number, 0 if not selected
            volunteersNeeded: data.volunteersNeeded,
            projectedVolunteerHours: data.projectedVolunteerHours,
            projectDescription: data.projectDescription,
            notes: data.notes,
            volunteerIds: (data.team || []).map((id) => parseInt(id, 10)).filter((id) => !isNaN(id)),
            toolsNeeded: Array.isArray(data.toolsNeeded)
                ? stripBlankLineItems(
                    sortToolsWithToolKitFirst(
                        data.toolsNeeded.map((item: any) =>
                            typeof item === "string"
                                ? { name: item, out: false, in: false }
                                : item
                        )
                    ) as Array<{ name: string; out: boolean; in: boolean }>
                )
                : data.toolsNeeded,
            suppliesNeeded: Array.isArray(data.suppliesNeeded)
                ? stripBlankLineItems(
                    data.suppliesNeeded.map((item: any) =>
                        typeof item === "string"
                            ? { name: item, out: false, in: false }
                            : item
                    )
                )
                : data.suppliesNeeded,
            workOrderHours: (() => {
                // Filter out completely empty entries
                const nonEmptyEntries = (data.workOrderHours || []).filter(
                    entry => entry.date || (entry.hoursWorked && entry.hoursWorked > 0)
                )
                
                // Format and return entries
                return nonEmptyEntries.map(entry => ({
                    date: entry.date ? formatDateForDB(entry.date) : null,
                    hoursWorked: entry.hoursWorked || 0,
                }))
            })(),
        }
        
        // For TeamLeaders editing, only send the volunteer IDs field
        const workOrderData = isTeamLeaderRestricted 
            ? { volunteerIds: baseWorkOrderData.volunteerIds }
            : baseWorkOrderData

        console.log("Submitting work order data:", workOrderData)

        try {
            // Handle note if changed - save to Project General Notes (before updating work order)
            const noteChanged = workOrderNote.trim() !== originalWorkOrderNote.trim()
            if (noteChanged && workOrderNote.trim() && workOrder?.home?.id) {
                try {
                    // Fetch current home details to get existing projectGeneralNotes
                    const homeResponse = await getHome(workOrder.home.id)
                    
                    if (homeResponse.success && homeResponse.data) {
                        const currentNotes = homeResponse.data.projectGeneralNotes || ""
                        const dateStamp = formatDateStamp()
                        const newNoteWithTimestamp = `${dateStamp} ${workOrderNote.trim()}`
                        
                        // Append new note to existing notes
                        const updatedNotes = currentNotes 
                            ? `${currentNotes}\n${newNoteWithTimestamp}`
                            : newNoteWithTimestamp

                        // Update home with appended notes
                        await updateHome(workOrder.home.id, {
                            projectGeneralNotes: updatedNotes
                        })
                    }
                } catch (error) {
                    console.error("Error saving note to Project General Notes:", error)
                    // Continue with work order save even if note save fails
                }
            }

            // Import the work order actions
            const { createWorkOrder, updateWorkOrder } = await import(
                "@/features/work-orders/api/work-orders.api"
            )

            let response
            if (mode === "edit" && workOrder?.id) {
                // Update existing work order
                response = await updateWorkOrder(workOrder.id, workOrderData as any)
                if (response.success) {
                    console.log(
                        "Work order updated successfully:",
                        response.data
                    )
                    const message = isTeamLeaderRestricted 
                        ? "Volunteers updated successfully" 
                        : "Work order updated successfully"
                    successToast(message)
                    
                    // Handle note if it was changed
                    if (noteChanged && workOrderNote.trim()) {
                        // Clear the note input after saving and reset original to empty
                        setWorkOrderNote("")
                        setOriginalWorkOrderNote("")
                    }
                    
                    // Mark form as clean and show success bar instead of navigating away
                    form.reset(form.getValues())
                    setToolsSuppliesLoaded(true)
                    setIsFormClean(true)
                    setShowSuccessBar(true)
                    setIsLoading(false)
                    
                    // Auto-hide success bar after 3 seconds (same as other pages)
                    setTimeout(() => {
                        setShowSuccessBar(false)
                    }, 3000)
                    
                    return // Return here to prevent navigation
                } else {
                    console.error(
                        "Failed to update work order:",
                        (response as any).error
                    )
                    errorToast(
                        (response as any).error ||
                            "Failed to update work order. Please try again."
                    )
                    setIsLoading(false)
                    return
                }
            } else {
                // Create new work order
                response = await createWorkOrder(baseWorkOrderData)
                if (response.success && response.data) {
                    successToast("Work order created successfully")
                    
                    // Navigate to the edit page for the newly created work order
                    setIsLoading(false)
                    const workOrderData = response.data as { id: number }
                    router.push(`/work-orders/edit/${workOrderData.id}`)
                    return
                } else {
                    console.error(
                        "Failed to create work order:",
                        (response as any).error
                    )
                    errorToast(
                        (response as any).error ||
                            "Failed to create work order. Please try again."
                    )
                    setIsLoading(false)
                    return
                }
            }
        } catch (error) {
            console.error("Error with work order operation:", error)
            // Show error toast for unexpected errors
            errorToast(
                "An unexpected error occurred. Please check all required fields and try again."
            )
            setIsLoading(false)
        }
    }

    const onError = (errors: any) => {
        // Show error toast for validation failures
        const errorMessages = Object.values(errors)
            .map((error: any) => error.message)
            .filter(Boolean)
        if (errorMessages.length > 0) {
            errorToast(
                `Please fill in all required fields: ${errorMessages.join(
                    ", "
                )}`
            )
        } else {
            errorToast("Please fill in all required fields before submitting.")
        }
    }

    // Show loading spinner if data is still loading
    if (isEditModeLoading || isCreateModeLoading) {
        return (
            <FormCard>
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground text-center mt-2">
                        Loading form data...
                    </p>
                </div>
            </FormCard>
        )
    }

    // Show error state if data failed to load
    if (hasDataLoadError) {
        return (
            <FormCard>
                <div className="flex flex-col items-center justify-center py-12">
                    <TypographyH4 className="text-center text-destructive mb-4">
                        Failed to load form data
                    </TypographyH4>
                    <p className="text-muted-foreground text-center mb-6">
                        Unable to load the required information. Please try
                        refreshing the page.
                    </p>
                    <Button
                        onClick={() => window.location.reload()}
                        className="px-6"
                    >
                        Refresh Page
                    </Button>
                </div>
            </FormCard>
        )
    }

    // Handler for sticky bar save button
    const handleStickySave = async () => {
        // Trigger validation and submit
        await form.handleSubmit(onSubmit, onError)()
    }

    return (
        <>
            <FormCard>
                {isTeamLeaderRestricted && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                            <strong>Team Leader Access:</strong> You can edit the Volunteers, Work Days, and Total Hours. Other fields are view-only.
                        </p>
                    </div>
                )}
                <FormProvider {...form}>
                    <DirtyFieldsProvider isFormClean={isFormClean}>
                        <Form {...form}>
                <form
                    className="pb-20"
                    onSubmit={form.handleSubmit(onSubmit, onError)}
                    onKeyDown={(e) => {
                        // Prevent form submission when Enter is pressed in any input
                        // But allow it for textarea fields and the home search input
                        if (e.key === "Enter") {
                            const target = e.target as HTMLElement
                            const isTextarea = target.tagName === "TEXTAREA"
                            const isHomeSearchInput =
                                target.id === "home-search-input"

                            if (!isTextarea && !isHomeSearchInput) {
                                e.preventDefault()
                                e.stopPropagation()
                            }
                        }
                    }}
                >
                    {/* Row 1: Home, Construction Manager, Date - 3 columns */}
                    <div className="grid grid-cols-1 gap-3 lg1250:grid-cols-3">
                        <FormField
                            control={form.control}
                            name="home"
                            render={({ field }) => (
                                <FormItem>
                                    <DirtyAwareLabel fieldName="home">Home</DirtyAwareLabel>
                                    <FormControl>
                                        <div
                                            className={cn(
                                                (isTeamLeaderRestricted || isLoadingHomes) &&
                                                    "opacity-60 pointer-events-none"
                                            )}
                                        >
                                            <SingleSelect
                                                options={homeOptions}
                                                selectedValue={field.value ?? ""}
                                                onSelectionChange={field.onChange}
                                                placeholder={
                                                    isLoadingHomes
                                                        ? "Loading homes..."
                                                        : homesData?.success === false
                                                        ? "Error loading homes"
                                                        : "Search for a home by name or address *"
                                                }
                                                searchPlaceholder="Search homes by name or address..."
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="ownerId"
                            render={({ field }) => (
                                <FormItem>
                                    <DirtyAwareLabel fieldName="ownerId">Construction Manager</DirtyAwareLabel>
                                    <FormControl>
                                        <div className={cn(isTeamLeaderRestricted && "opacity-60 pointer-events-none")}>
                                            <SingleSelectWithFilters
                                                options={ownerOptions}
                                                selectedValue={
                                                    field.value && field.value > 0
                                                        ? field.value.toString()
                                                        : ""
                                                }
                                                onSelectionChange={(value) => {
                                                    field.onChange(
                                                        value ? Number(value) : 0
                                                    )
                                                }}
                                                placeholder="Select construction manager"
                                                searchPlaceholder="Search construction managers..."
                                                organizationOptions={ownerOrganizationOptions}
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Work Days & Hours - Multi-day tracking */}
                        <div className="space-y-3">
                            {/* Field Headers */}
                            <div className="flex gap-2 items-center">
                                <div className="w-52">
                                    <Label className="text-sm font-medium" style={{ color: "#003764" }}>Work Day</Label>
                                </div>
                                <div className="w-24">
                                    <Label className="text-sm font-medium" style={{ color: "#003764" }}>Total Hours</Label>
                                </div>
                                <div className="w-10"></div>
                            </div>

                            {/* Work day entries */}
                            <div className="space-y-3">
                                {workOrderHours.map((entry, index) => (
                                    <div key={index} className="flex gap-2 items-center">
                                        {/* Date Picker */}
                                        <WorkOrderHoursDatepicker
                                            value={entry.date}
                                            onSelect={(date) => {
                                                const newHours = [...workOrderHours]
                                                newHours[index] = {
                                                    ...newHours[index],
                                                    date: date || null
                                                }
                                                setWorkOrderHours(newHours)
                                                form.setValue('workOrderHours', newHours, { shouldDirty: true })
                                            }}
                                            className="w-52 flex-shrink-0 border-input"
                                            isError={isDateRequired(index)}
                                            isModified={isWorkHoursFieldModified(index, 'date') && !isDateRequired(index)}
                                            placeholder={isDateRequired(index) ? "Date required" : "Pick date"}
                                            storageFormat="date"
                                        />

                                        {/* Hours Input */}
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            placeholder="Hours"
                                            value={entry.hoursWorked || ""}
                                            onChange={(e) => {
                                                const newHours = [...workOrderHours]
                                                newHours[index] = {
                                                    ...newHours[index],
                                                    hoursWorked: parseFloat((e.target as HTMLInputElement).value) || 0
                                                }
                                                setWorkOrderHours(newHours)
                                                form.setValue('workOrderHours', newHours, { shouldDirty: true })
                                            }}
                                            className={cn(
                                                "w-24 flex-shrink-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                                isWorkHoursFieldModified(index, 'hoursWorked') && "border-2"
                                            )}
                                            style={isWorkHoursFieldModified(index, 'hoursWorked') ? { borderColor: '#9c27b0' } : undefined}
                                        />

                                        {/* Remove button (show when there are 2+ rows) */}
                                        {workOrderHours.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-10 w-10 flex-shrink-0"
                                                onClick={() => {
                                                    const newHours = workOrderHours.filter((_, i) => i !== index)
                                                    setWorkOrderHours(newHours)
                                                    form.setValue('workOrderHours', newHours, { shouldDirty: true })
                                                }}
                                                showLoader={false}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                ))}

                                {/* Add Work Days Range */}
                                <div className="w-full max-w-md">
                                    <DateRangePickerControl
                                        label="Add Work Days"
                                        value={addWorkDaysRange}
                                        onChange={(nextRange) => {
                                            setAddWorkDaysRange(nextRange)
                                            if (nextRange.from && nextRange.to) {
                                                addWorkDaysFromRange(nextRange)
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Row 2: Team Lead/Team Name, Volunteers Needed/Projected Hours, Volunteers (spanning 2 rows) */}
                    <div className="grid grid-cols-1 gap-3 lg1250:grid-cols-3">
                        <div className="space-y-3">
                            {/* Team Lead (top) */}
                            <FormField
                                control={form.control}
                                name="teamLeadId"
                                render={({ field }) => (
                                    <FormItem>
                                        <DirtyAwareLabel fieldName="teamLeadId">Team Lead</DirtyAwareLabel>
                                        <FormControl>
                                            <div className={cn(isTeamLeaderRestricted && "opacity-60 pointer-events-none")}>
                                                <SingleSelectWithFilters
                                                    options={teamLeadOptions}
                                                    selectedValue={
                                                        field.value && field.value > 0
                                                            ? field.value.toString()
                                                            : ""
                                                    }
                                                    onSelectionChange={(value) => {
                                                        field.onChange(value ? Number(value) : 0)
                                                    }}
                                                    placeholder="Select team lead"
                                                    searchPlaceholder="Search team leads..."
                                                    organizationOptions={organizationOptions}
                                                />
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Team Name (bottom) */}
                            <FormField
                                control={form.control}
                                name="teamName"
                                render={({ field }) => (
                                    <FormItem>
                                        <DirtyAwareLabel fieldName="teamName">Team Name</DirtyAwareLabel>
                                        <FormControl>
                                            <DirtyAwareInput fieldName="teamName">
                                                <Input
                                                    placeholder="Enter team name"
                                                    {...field}
                                                    disabled={isTeamLeaderRestricted}
                                                />
                                            </DirtyAwareInput>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="space-y-3">
                            {/* Volunteers Needed (top) */}
                            <FormField
                                control={form.control}
                                name="volunteersNeeded"
                                render={({ field }) => (
                                    <FormItem>
                                        <DirtyAwareLabel fieldName="volunteersNeeded">
                                            Volunteers Needed (Number)
                                        </DirtyAwareLabel>
                                    <FormControl>
                                        <DirtyAwareInput fieldName="volunteersNeeded">
                                            <Input
                                                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                placeholder="Volunteers Needed"
                                                value={field.value || ""}
                                                disabled={isTeamLeaderRestricted}
                                            onChange={(e) => {
                                                const value = (
                                                    e.target as HTMLInputElement
                                                ).value

                                                // Clear any previous error
                                                setVolunteersNeededError("")

                                                // Handle empty string
                                                if (
                                                    value === "" ||
                                                    value === null ||
                                                    value === undefined
                                                ) {
                                                    field.onChange(0)
                                                    return
                                                }

                                                // Check if input contains only numbers
                                                const isValidNumber =
                                                    /^\d+$/.test(value)

                                                if (isValidNumber) {
                                                    const numValue = parseInt(
                                                        value,
                                                        10
                                                    )
                                                    if (numValue >= 0) {
                                                        field.onChange(numValue)
                                                    }
                                                } else {
                                                    // Show error for invalid input
                                                    setVolunteersNeededError(
                                                        "Please enter a valid number"
                                                    )
                                                }
                                            }}
                                        />
                                        </DirtyAwareInput>
                                    </FormControl>
                                    {volunteersNeededError && (
                                        <p className="text-sm text-destructive mt-1">
                                            {volunteersNeededError}
                                        </p>
                                    )}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                            {/* Projected Hours (bottom) */}
                            <FormField
                                control={form.control}
                                name="projectedVolunteerHours"
                                render={({ field }) => (
                                    <FormItem>
                                        <DirtyAwareLabel fieldName="projectedVolunteerHours">
                                            Projected Volunteer Hours (Number)
                                        </DirtyAwareLabel>
                                    <FormControl>
                                        <Input
                                            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            placeholder="Projected Volunteer Hours"
                                            value={field.value || ""}
                                            disabled={isTeamLeaderRestricted}
                                            onChange={(e) => {
                                                const value = (
                                                    e.target as HTMLInputElement
                                                ).value

                                                // Clear any previous error
                                                setProjectedVolunteerHoursError(
                                                    ""
                                                )

                                                // Handle empty string
                                                if (
                                                    value === "" ||
                                                    value === null ||
                                                    value === undefined
                                                ) {
                                                    field.onChange(0)
                                                    return
                                                }

                                                // Check if input contains only numbers
                                                const isValidNumber =
                                                    /^\d+$/.test(value)

                                                if (isValidNumber) {
                                                    const numValue = parseInt(
                                                        value,
                                                        10
                                                    )
                                                    if (numValue >= 0) {
                                                        field.onChange(numValue)
                                                    }
                                                } else {
                                                    // Show error for invalid input
                                                    setProjectedVolunteerHoursError(
                                                        "Please enter a valid number"
                                                    )
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    {projectedVolunteerHoursError && (
                                        <p className="text-sm text-destructive mt-1">
                                            {projectedVolunteerHoursError}
                                        </p>
                                    )}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        </div>

                        {/* Volunteers column - taller to match the height of Team Lead + Team Name */}
                        <div>
                            <FormField
                                control={form.control}
                                name="team"
                                render={({ field }) => (
                                    <FormItem>
                                        <DirtyAwareLabel fieldName="team">Volunteers</DirtyAwareLabel>
                                        <FormControl>
                                            <MultiSelectWithFilters
                                                options={
                                                    volunteersData?.success &&
                                                    volunteersData.data
                                                        ? volunteersData.data.volunteers.map(
                                                              (volunteer: any) => ({
                                                                  id: volunteer.id.toString(),
                                                                  name: volunteer.user.fullname,
                                                                  role: volunteer.user.role?.name || "Unknown",
                                                                  organization: volunteer.user.organizationName || undefined,
                                                                  // Use availability dates as proxy for serving dates until volunteer dates are available
                                                                  arrivalDate: volunteer.user.availabilityStart ? new Date(volunteer.user.availabilityStart).toISOString().split('T')[0] : undefined,
                                                                  departureDate: volunteer.user.availabilityEnd ? new Date(volunteer.user.availabilityEnd).toISOString().split('T')[0] : undefined,
                                                              })
                                                          )
                                                        : []
                                                }
                                                selectedValues={field.value ? field.value : []}
                                                onSelectionChange={field.onChange}
                                                placeholder="Select volunteers"
                                                searchPlaceholder="Search volunteers..."
                                                customHeight="h-[156px]"
                                                organizationOptions={organizationOptions}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    {/* Project Description, Tools, Supplies - Full Width */}
                    <div className="grid grid-cols-1 gap-5 lg1250:grid-cols-2">
                        <FormField
                            control={form.control}
                            name="projectDescription"
                            render={({ field }) => (
                                <FormItem className="lg1250:col-span-2 space-y-1">
                                    <DirtyAwareLabel fieldName="projectDescription" className="text-sm pl-1">
                                        Project Description
                                    </DirtyAwareLabel>
                                    <FormControl>
                                        <DirtyAwareInput fieldName="projectDescription">
                                            <Textarea
                                                placeholder="Project Description"
                                                rows={4}
                                                {...field}
                                                disabled={isTeamLeaderRestricted}
                                            />
                                        </DirtyAwareInput>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="toolsNeeded"
                            render={({ field }) => (
                                <FormItem className={cn("lg1250:row-span-2", isTeamLeaderRestricted && "opacity-60 pointer-events-none")}>
                                    {!selectedWorkScope && !(mode === "edit" && workOrder?.workOrderType) ? (
                                        <FormItem className="lg1250:col-span-2 space-y-1">
                                            <DirtyAwareLabel fieldName="toolsNeeded" className="text-sm pl-1">
                                                Tools Needed
                                            </DirtyAwareLabel>
                                            <FormControl>
                                                <div className="text-sm text-muted-foreground p-3 border rounded-md bg-muted/50 h-[200px] flex items-center justify-center">
                                                    Please select a work scope above to manage tools and supplies
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    ) : (
                                        <div className="space-y-1">
                                            <CheckboxList
                                                title="Tools"
                                                options={stripBlankLineNames(dbWorkScope?.toolsNeeded || [])}
                                                value={Array.isArray(field.value) 
                                                    ? field.value.map((item: any) => typeof item === 'string' ? item : item?.name || item)
                                                    : []}
                                                onChange={(newValue: string[]) => {
                                                    // Convert strings to object format for saving
                                                    const objectValue = newValue.map(name => ({
                                                        name,
                                                        out: false,
                                                        in: false
                                                    }))
                                                    field.onChange(objectValue)
                                                }}
                                                onAddCustom={() => openAddCustomModal("tool")}
                                                fieldName="toolsNeeded"
                                            />
                                        </div>
                                    )}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="suppliesNeeded"
                            render={({ field }) => (
                                <FormItem className={cn(isTeamLeaderRestricted && "lg1250:row-span-2 opacity-60 pointer-events-none")}>
                                    {!selectedWorkScope && !(mode === "edit" && workOrder?.workOrderType) ? (
                                        <FormItem className="lg1250:col-span-2 space-y-1">
                                            <DirtyAwareLabel fieldName="suppliesNeeded" className="text-sm pl-1">
                                                Supplies Needed
                                            </DirtyAwareLabel>
                                            <FormControl>
                                                <div className="text-sm text-muted-foreground p-3 border rounded-md bg-muted/50 h-[200px] flex items-center justify-center">
                                                    Please select a work scope above to manage tools and supplies
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    ) : (
                                        <div className="space-y-1">
                                            <CheckboxList
                                                title="Supplies"
                                                options={stripBlankLineNames(dbWorkScope?.suppliesNeeded || [])}
                                                value={Array.isArray(field.value) 
                                                    ? field.value.map((item: any) => typeof item === 'string' ? item : item?.name || item)
                                                    : []}
                                                onChange={(newValue: string[]) => {
                                                    // Convert strings to object format for saving
                                                    const objectValue = newValue.map(name => ({
                                                        name,
                                                        out: false,
                                                        in: false
                                                    }))
                                                    field.onChange(objectValue)
                                                }}
                                                onAddCustom={() => openAddCustomModal("supply")}
                                                fieldName="suppliesNeeded"
                                            />
                                        </div>
                                    )}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    {/* Notes/Status/Next Steps - Full Width */}
                    {canAddNotes && (
                        <div className="border-t border-gray-200 pt-2 pb-2 print:hidden">
                            <h3
                                className="text-lg font-semibold mb-2 print:mb-2"
                                style={{ color: "#003764" }}
                            >
                                Notes/Status/Next Steps
                            </h3>
                            <div>
                                <Label htmlFor="workOrderNote" className="text-sm font-medium text-gray-700">
                                    Add a note about work done. This will be added to Home Details Project General Notes.
                                </Label>
                                <Textarea
                                    id="workOrderNote"
                                    placeholder="Notes Status Next Steps"
                                    rows={6}
                                    value={workOrderNote}
                                    onChange={(e) => setWorkOrderNote(e.target.value)}
                                    disabled={isTeamLeaderRestricted}
                                    className="mt-1 resize-none"
                                />
                            </div>
                        </div>
                    )}

                </form>
            </Form>

            {/* Add Custom Tool/Supply Modal */}
            <Dialog
                open={isAddCustomModalOpen}
                onOpenChange={setIsAddCustomModalOpen}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Add Custom{" "}
                            {modalType === "supply" ? "Supply" : "Tool"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="customTool">
                                {modalType === "supply" ? "Supply" : "Tool"}{" "}
                                Name
                            </Label>
                            <Input
                                id="customTool"
                                placeholder={`Enter ${
                                    modalType === "supply" ? "supply" : "tool"
                                } name`}
                                value={customToolInput}
                                onChange={(e) =>
                                    setCustomToolInput(
                                        (e.target as HTMLInputElement).value
                                    )
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault()
                                        handleAddCustomTool()
                                    }
                                }}
                            />
                        </div>

                        <div className="flex gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsAddCustomModalOpen(false)
                                    setCustomToolInput("")
                                    setModalType("tool")
                                }}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAddCustomTool}
                                className="flex-1"
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
                    </DirtyFieldsProvider>
                </FormProvider>
            </FormCard>
            <StickySaveBar
                isVisible={hasUnsavedChanges || showSuccessBar}
                isSuccess={showSuccessBar}
                isSubmitting={isLoading}
                onSave={handleStickySave}
            />
        </>
    )
}

export default WorkOrderForm
